import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { BRAND, HERO_BG } from "../../data";
import { Logo } from "./Logo";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.5 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 800], [0, 160]);
  const contentY = useTransform(scrollY, [0, 800], [0, 90]);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden flex items-center"
    >
      {/* Parallax background */}
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10 will-change-transform">
        <img src={HERO_BG} alt="" className="w-full h-[120%] object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]" />
        <div className="absolute -left-40 top-1/3 w-[520px] h-[520px] rounded-full bg-[#b533ff]/25 blur-[140px]" />
        <div className="absolute right-0 bottom-0 w-[420px] h-[420px] rounded-full bg-[#d946ef]/20 blur-[130px]" />
      </motion.div>

      <motion.div
        style={{ y: contentY }}
        className="relative max-w-7xl mx-auto w-full px-5 sm:px-8 pt-28 pb-16"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-xs sm:text-sm uppercase tracking-[0.35em] text-[#E879F9] font-bold mb-8"
          data-testid="hero-eyebrow"
        >
          {BRAND.eyebrow}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.15, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 relative w-fit"
        >
          <Logo
            className="h-24 sm:h-36 lg:h-44"
            showProductions
            productionsClassName="mt-4 pl-1 tracking-[0.4em] text-sm sm:text-base text-[#E879F9]"
          />
        </motion.div>

        <h1 className="font-display font-black uppercase leading-[0.9] tracking-tighter text-white">
          {BRAND.taglineLines.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="inline-block text-5xl sm:text-7xl lg:text-[7.5rem]"
              >
                {i === 1 ? (
                  <span className="text-[#b533ff] text-glow">{line}</span>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-col lg:flex-row lg:items-end gap-8 lg:justify-between"
        >
          <p className="max-w-xl text-base sm:text-lg text-gray-300 font-light leading-relaxed">
            {BRAND.tagline}
          </p>
          <button
            data-testid="hero-cta"
            onClick={() =>
              document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })
            }
            className="group inline-flex items-center gap-3 self-start lg:self-auto px-7 py-4 rounded-full border border-white/20 hover:border-[#b533ff] hover:bg-[#b533ff]/10 transition-colors duration-300"
          >
            <span className="text-sm uppercase tracking-[0.2em] text-white font-semibold">
              Book Marta
            </span>
            <ArrowDownRight
              size={18}
              className="text-[#b533ff] group-hover:translate-x-0.5 group-hover:translate-y-0.5 transition-transform duration-300"
            />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
